import { useState } from 'react';
import PrivateLayout from '@/components/layout/auth/PrivateLayout';
import { useI18n } from '@/hooks/useI18n';
import { Button } from '@/components/ui/button';
import { useOrphanItineraries } from '@/hooks/useOrphanItineraries';
import { CreateOrphanItineraryModal } from '@/lib/itineraries/CreateOrphanItineraryModal';
import { LinkToTravelRequestModal } from '@/lib/itineraries/LinkToTravelRequestModal';
import { OrphanItineraryCard } from '@/lib/itineraries/OrphanItineraryCard';
import { Calendar, Lightbulb, Plus, RefreshCw } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const Playground = () => {
  const { t } = useI18n();
  const navigate = useNavigate();

  const {
    orphanItineraries,
    loading,
    createOrphanItinerary,
    linkItineraryToTravelRequest,
    deleteOrphanItinerary,
    refreshData,
  } = useOrphanItineraries();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [selectedItinerary, setSelectedItinerary] = useState<{ id: string; title: string } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itineraryToDelete, setItineraryToDelete] = useState<string | null>(null);

  const handleCreateItinerary = async (payload: any) => {
    await createOrphanItinerary(payload);
  };

  const handleOpenLinkModal = (id: string, title: string) => {
    setSelectedItinerary({ id, title });
    setIsLinkModalOpen(true);
  };

  const handleCloseLinkModal = () => {
    setIsLinkModalOpen(false);
    setSelectedItinerary(null);
  };

  const handleLinkToTravelRequest = async (travelRequestId: string) => {
    if (!selectedItinerary) return;
    const result = await linkItineraryToTravelRequest(selectedItinerary.id, travelRequestId);
    if (result) {
      navigate('/dashboard?tab=draft');
    }
  };

  const handleEdit = (id: string) => {
    navigate(`/itinerary-builder?itineraryId=${id}`);
  };

  const handleDeleteClick = (id: string) => {
    setItineraryToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (itineraryToDelete) {
      await deleteOrphanItinerary(itineraryToDelete);
      setDeleteDialogOpen(false);
      setItineraryToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItineraryToDelete(null);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshData();
    } finally {
      setRefreshing(false);
    }
  };

  const countLabel = orphanItineraries.length === 1
    ? `${orphanItineraries.length} ${t('playground.itinerary')}`
    : `${orphanItineraries.length} ${t('playground.itineraries')}`;

  return (
    <PrivateLayout
      pageTitle={t("playground.title")}
      pageDescription={t("playground.description-title")}
      showBackButton={false}
      headerAction={
        <Button
          variant="default"
          size="lg"
          className="h-8 px-3"
          onClick={() => setIsCreateModalOpen(true)}
        >
          <Plus className="h-4 w-4 mr-1" />
          {t("playground.createItinerary")}
        </Button>
      }
    >
      {/* Header */}
      <div className="mb-6 space-y-4 sm:space-y-0 sm:flex sm:justify-between sm:items-center">
        <div className="flex items-center gap-2">
          <h6 className="text-gray-500">{countLabel}</h6>
        </div>
        <div className="flex items-center justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="flex items-center gap-2"
          >
            <RefreshCw
              className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
            />
            <span className="hidden sm:inline">{t("dashboard.refresh")}</span>
          </Button>
        </div>
      </div>
      <div className="max-w-7xl mx-auto">
        {/* Loading state */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-64 w-full" />
            ))}
          </div>
        ) : orphanItineraries.length === 0 ? (
          // Empty state
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <Calendar className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-xl font-medium text-gray-900 mb-2">
              {t("playground.noItineraries")}
            </h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {t("playground.noItinerariesDesc")}
            </p>
            <Button onClick={() => setIsCreateModalOpen(true)}>
              {t("playground.createFirstItinerary")}
            </Button>
          </div>
        ) : (
          // Itineraries grid
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {orphanItineraries.map((itinerary, index) => (
              <div
                key={itinerary.id}
                className="animate-in fade-in zoom-in-95 duration-500"
                style={{
                  animationDelay: `${index * 50}ms`,
                  animationFillMode: "backwards",
                }}
              >
                <OrphanItineraryCard
                  itinerary={itinerary}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                  onLink={handleOpenLinkModal}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Itinerary Modal */}
      <CreateOrphanItineraryModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateItinerary}
      />

      {/* Link to Travel Request Modal */}
      {selectedItinerary && (
        <LinkToTravelRequestModal
          isOpen={isLinkModalOpen}
          onClose={handleCloseLinkModal}
          onLink={handleLinkToTravelRequest}
          itineraryId={selectedItinerary.id}
          itineraryTitle={selectedItinerary.title}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("playground.deleteConfirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("playground.deleteConfirmMessage")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDeleteCancel}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PrivateLayout>
  );
};

export default Playground;
